import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../utils/api';
import { 
  Plus, Building2, MapPin, User, ChevronDown, ChevronRight, 
  Edit2, Trash2, X, Phone, Mail, FileText, Check, AlertTriangle,
  Landmark, Building, Users, TreePine, Home
} from 'lucide-react';

// Category definitions - must match backend enum
const CATEGORIES = [
  { value: 'CASINO', label: 'Casinos & Resorts', icon: Landmark, color: 'text-amber-600' },
  { value: 'CORPORATE', label: 'Corporate', icon: Building, color: 'text-blue-600' },
  { value: 'PROMOTER', label: 'Promoters & Agencies', icon: Users, color: 'text-purple-600' },
  { value: 'MUNICIPAL', label: 'Municipal / Government', icon: TreePine, color: 'text-green-600' },
  { value: 'PRIVATE', label: 'Private Events', icon: Home, color: 'text-rose-600' },
];

const getCategoryInfo = (value) => CATEGORIES.find(c => c.value === value) || CATEGORIES[0];

// Colorblind-friendly palette
const CLIENT_COLORS = [
  { value: '#FCD5CE', label: 'Peach' },
  { value: '#FFE5B4', label: 'Cream' },
  { value: '#FFF3CD', label: 'Yellow' },
  { value: '#D4EDDA', label: 'Mint' },
  { value: '#CCE5FF', label: 'Sky' },
  { value: '#E2D9F3', label: 'Lavender' },
  { value: '#F5F5F5', label: 'Gray' },
  { value: '#FFDAB9', label: 'Coral' },
];

// US Timezones
const TIMEZONES = [
  { value: 'America/Los_Angeles', label: 'Pacific (Los Angeles)' },
  { value: 'America/Denver', label: 'Mountain (Denver)' },
  { value: 'America/Chicago', label: 'Central (Chicago)' },
  { value: 'America/New_York', label: 'Eastern (New York)' },
  { value: 'America/Phoenix', label: 'Arizona (Phoenix)' },
  { value: 'America/Anchorage', label: 'Alaska (Anchorage)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (Honolulu)' },
];

function ConfirmDeleteModal({ name, type, details, onConfirm, onClose }) {
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const handleConfirm = async () => {
    if (confirmText !== 'DELETE') return;
    setDeleting(true);
    await onConfirm();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md animate-slide-up">
        <div className="p-4 border-b bg-red-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-red-900">Permanently Delete {type}</h2>
              <p className="text-sm text-red-700">This action cannot be undone</p>
            </div>
          </div>
        </div>
        <div className="p-4 space-y-4">
          <p className="text-gray-700">
            You are about to permanently delete <strong>{name}</strong> and all associated data including:
          </p>
          <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
            {details.map((d, i) => <li key={i}>{d}</li>)}
          </ul>
          <div>
            <label className="label">Type DELETE to confirm</label>
            <input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
              className="input font-mono"
              placeholder="DELETE"
              autoFocus
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button
              onClick={handleConfirm}
              disabled={confirmText !== 'DELETE' || deleting}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-red-700"
            >
              {deleting ? 'Deleting...' : 'Permanently Delete'}
            </button>
            <button onClick={onClose} className="btn-secondary flex-1" disabled={deleting}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ColorPicker({ value, onChange }) {
  return (
    <div>
      <label className="label">Schedule Color</label>
      <div className="flex flex-wrap gap-2 mt-1">
        {CLIENT_COLORS.map((color) => (
          <button
            key={color.value}
            type="button"
            onClick={() => onChange(color.value)}
            className={`w-8 h-8 rounded-lg border-2 flex items-center justify-center transition-all ${
              value === color.value ? 'border-gray-800 scale-110' : 'border-gray-300 hover:border-gray-400'
            }`}
            style={{ backgroundColor: color.value }}
            title={color.label}
          >
            {value === color.value && <Check className="w-4 h-4 text-gray-700" />}
          </button>
        ))}
        <button
          type="button"
          onClick={() => onChange(null)}
          className={`w-8 h-8 rounded-lg border-2 flex items-center justify-center transition-all ${
            !value ? 'border-gray-800 scale-110' : 'border-gray-300 hover:border-gray-400'
          }`}
          style={{ backgroundColor: '#fff' }}
          title="No color"
        >
          {!value && <X className="w-4 h-4 text-gray-400" />}
        </button>
      </div>
    </div>
  );
}

function CategoryPicker({ value, onChange }) {
  return (
    <div>
      <label className="label">Category *</label>
      <div className="grid grid-cols-1 gap-2 mt-1">
        {CATEGORIES.map((cat) => {
          const Icon = cat.icon;
          return (
            <button
              key={cat.value}
              type="button"
              onClick={() => onChange(cat.value)}
              className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all text-left ${
                value === cat.value ? 'border-hub-600 bg-hub-50' : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <Icon className={`w-5 h-5 ${cat.color}`} />
              <span className="font-medium">{cat.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ClientModal({ client, onSave, onClose }) {
  const [name, setName] = useState(client?.name || '');
  const [category, setCategory] = useState(client?.category || 'CASINO');
  const [billingAddress, setBillingAddress] = useState(client?.billingAddress || '');
  const [paymentTerms, setPaymentTerms] = useState(client?.paymentTerms || '');
  const [notes, setNotes] = useState(client?.notes || '');
  const [color, setColor] = useState(client?.color || null);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (client?.id) {
        await api.put(`/clients/${client.id}`, { name, category, billingAddress, paymentTerms, notes, color });
      } else {
        await api.post('/clients', { name, category, billingAddress, paymentTerms, notes, color });
      }
      onSave();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md my-8 animate-slide-up">
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">{client?.id ? 'Edit Client' : 'New Client'}</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
          <CategoryPicker value={category} onChange={setCategory} />
          <div>
            <label className="label">Name *</label>
            <input value={name} onChange={e => setName(e.target.value)} className="input" required />
          </div>
          <ColorPicker value={color} onChange={setColor} />
          <div>
            <label className="label">Billing Address</label>
            <textarea value={billingAddress} onChange={e => setBillingAddress(e.target.value)} className="input" rows={2} />
          </div>
          <div>
            <label className="label">Payment Terms</label>
            <input value={paymentTerms} onChange={e => setPaymentTerms(e.target.value)} className="input" placeholder="Net 30" />
          </div>
          <div>
            <label className="label">Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} className="input" rows={2} />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="submit" className="btn-primary flex-1" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function VenueModal({ venue, clientId, onSave, onClose }) {
  const [name, setName] = useState(venue?.name || '');
  const [address, setAddress] = useState(venue?.address || '');
  const [timezone, setTimezone] = useState(venue?.timezone || 'America/Los_Angeles');
  const [notes, setNotes] = useState(venue?.notes || '');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (venue?.id) {
        await api.put(`/venues/${venue.id}`, { name, address, timezone, notes });
      } else {
        await api.post('/venues', { clientId, name, address, timezone, notes });
      }
      onSave();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md animate-slide-up">
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">{venue?.id ? 'Edit Venue' : 'New Venue'}</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="label">Name *</label>
            <input value={name} onChange={e => setName(e.target.value)} className="input" required />
          </div>
          <div>
            <label className="label">Address</label>
            <textarea value={address} onChange={e => setAddress(e.target.value)} className="input" rows={2} />
          </div>
          <div>
            <label className="label">Timezone *</label>
            <select value={timezone} onChange={e => setTimezone(e.target.value)} className="input">
              {TIMEZONES.map(tz => (
                <option key={tz.value} value={tz.value}>{tz.label}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">Gig times will display in this timezone</p>
          </div>
          <div>
            <label className="label">Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} className="input" rows={2} />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="submit" className="btn-primary flex-1" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function LocationModal({ location, venueId, onSave, onClose }) {
  const [name, setName] = useState(location?.name || '');
  const [notes, setNotes] = useState(location?.notes || '');
  const [defaultClientBilled, setDefaultClientBilled] = useState(location?.defaultClientBilled || '');
  const [defaultPayout, setDefaultPayout] = useState(location?.defaultPayout || '');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = { 
        name, 
        notes,
        defaultClientBilled: defaultClientBilled ? parseFloat(defaultClientBilled) : null,
        defaultPayout: defaultPayout ? parseFloat(defaultPayout) : null
      };
      if (location?.id) {
        await api.put(`/locations/${location.id}`, data);
      } else {
        await api.post('/locations', { venueId, ...data });
      }
      onSave();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md animate-slide-up">
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">{location?.id ? 'Edit Location' : 'New Location'}</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="label">Name *</label>
            <input value={name} onChange={e => setName(e.target.value)} className="input" required placeholder="e.g. Lobby Bar, Pool Deck" />
          </div>
          <div>
            <label className="label">Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} className="input" rows={2} placeholder="Load-in info, stage details, etc." />
          </div>
          
          {/* Default Billing */}
          <div className="pt-2 border-t">
            <p className="text-sm font-medium text-gray-700 mb-3">Default Billing (optional)</p>
            <p className="text-xs text-gray-500 mb-3">Auto-fills when creating new gigs at this location</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">Client Billed</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input 
                    type="number" 
                    value={defaultClientBilled} 
                    onChange={e => setDefaultClientBilled(e.target.value)} 
                    className="input pl-7" 
                    placeholder="0" 
                    min="0" 
                    step="1" 
                  />
                </div>
              </div>
              <div>
                <label className="label">Contractor Payout</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input 
                    type="number" 
                    value={defaultPayout} 
                    onChange={e => setDefaultPayout(e.target.value)} 
                    className="input pl-7" 
                    placeholder="0" 
                    min="0" 
                    step="1" 
                  />
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex gap-3 pt-2">
            <button type="submit" className="btn-primary flex-1" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ContactModal({ contact, parentId, parentType, onSave, onClose }) {
  const [name, setName] = useState(contact?.name || '');
  const [title, setTitle] = useState(contact?.title || '');
  const [email, setEmail] = useState(contact?.email || '');
  const [phone, setPhone] = useState(contact?.phone || '');
  const [notes, setNotes] = useState(contact?.notes || '');
  const [isPrimary, setIsPrimary] = useState(contact?.isPrimary || false);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const endpoint = parentType === 'client' ? '/clients/contacts' : '/venues/contacts';
      const parentKey = parentType === 'client' ? 'clientId' : 'venueId';
      if (contact?.id) {
        await api.put(`${endpoint}/${contact.id}`, { name, title, email, phone, notes, isPrimary });
      } else {
        await api.post(endpoint, { [parentKey]: parentId, name, title, email, phone, notes, isPrimary });
      }
      onSave();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md animate-slide-up">
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">{contact?.id ? 'Edit Contact' : 'New Contact'}</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="label">Name *</label>
            <input value={name} onChange={e => setName(e.target.value)} className="input" required />
          </div>
          <div>
            <label className="label">Title/Role</label>
            <input value={title} onChange={e => setTitle(e.target.value)} className="input" placeholder="e.g. Entertainment Director" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="input" />
            </div>
            <div>
              <label className="label">Phone</label>
              <input value={phone} onChange={e => setPhone(e.target.value)} className="input" />
            </div>
          </div>
          <div>
            <label className="label">Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} className="input" rows={2} />
          </div>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={isPrimary} onChange={e => setIsPrimary(e.target.checked)} className="rounded" />
            <span className="text-sm">Primary contact</span>
          </label>
          <div className="flex gap-3 pt-2">
            <button type="submit" className="btn-primary flex-1" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ContactCard({ contact, onEdit, onDelete }) {
  return (
    <div className="flex items-center justify-between p-2 bg-white border rounded-lg">
      <div className="flex items-center gap-2">
        <User className="w-4 h-4 text-gray-400" />
        <div>
          <p className="text-sm font-medium">
            {contact.name}
            {contact.isPrimary && <span className="ml-2 text-xs text-hub-600">(Primary)</span>}
          </p>
          {contact.title && <p className="text-xs text-gray-500">{contact.title}</p>}
          <div className="flex gap-3 mt-1">
            {contact.phone && (
              <a href={`tel:${contact.phone}`} className="text-xs text-gray-500 hover:text-hub-600 flex items-center gap-1">
                <Phone className="w-3 h-3" />{contact.phone}
              </a>
            )}
            {contact.email && (
              <a href={`mailto:${contact.email}`} className="text-xs text-gray-500 hover:text-hub-600 flex items-center gap-1">
                <Mail className="w-3 h-3" />{contact.email}
              </a>
            )}
          </div>
        </div>
      </div>
      <div className="flex gap-1">
        <button onClick={onEdit} className="p-1 hover:bg-gray-100 rounded"><Edit2 className="w-3 h-3" /></button>
        <button onClick={onDelete} className="p-1 hover:bg-red-100 rounded text-red-600"><Trash2 className="w-3 h-3" /></button>
      </div>
    </div>
  );
}

export default function AdminClients() {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);
  const [expandedClients, setExpandedClients] = useState({});
  const [expandedVenues, setExpandedVenues] = useState({});
  const [activeCategory, setActiveCategory] = useState('ALL');

  const isOwner = user?.role === 'OWNER';

  const loadClients = () => {
    api.get('/clients?include=venues,contacts').then(setClients).finally(() => setLoading(false));
  };

  useEffect(() => { loadClients(); }, []);

  const toggleClient = (id) => setExpandedClients(p => ({ ...p, [id]: !p[id] }));
  const toggleVenue = (id) => setExpandedVenues(p => ({ ...p, [id]: !p[id] }));

  const deleteContact = async (type, id) => {
    if (!confirm('Delete this contact?')) return;
    try {
      await api.delete(`/${type}s/contacts/${id}`);
      loadClients();
    } catch (err) {
      alert(err.message);
    }
  };

  const deactivateClient = async (id, name) => {
    if (!confirm(`Deactivate "${name}"? This will hide the client but preserve historical data.`)) return;
    try {
      await api.delete(`/clients/${id}`);
      loadClients();
    } catch (err) {
      alert(err.message);
    }
  };

  const permanentlyDeleteClient = async (id) => {
    try {
      await api.delete(`/clients/${id}/permanent`, { confirm: 'DELETE' });
      setDeleteModal(null);
      loadClients();
    } catch (err) {
      alert(err.message);
      setDeleteModal(null);
    }
  };

  const deleteVenue = async (id, name) => {
    if (!confirm(`Deactivate "${name}"? This will hide the venue but preserve historical data.`)) return;
    try {
      await api.delete(`/venues/${id}`);
      loadClients();
    } catch (err) {
      alert(err.message);
    }
  };

  const filteredClients = activeCategory === 'ALL' 
    ? clients 
    : clients.filter(c => (c.category || 'CASINO') === activeCategory);

  const categoryCounts = CATEGORIES.reduce((acc, cat) => {
    acc[cat.value] = clients.filter(c => (c.category || 'CASINO') === cat.value).length;
    return acc;
  }, {});

  if (loading) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-hub-200 border-t-hub-600 rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Clients & Venues</h1>
        <button onClick={() => setModal({ type: 'client' })} className="btn-primary">
          <Plus className="w-4 h-4" />Add Client
        </button>
      </div>

      {/* Category Tabs */}
      <div className="flex flex-wrap gap-2 border-b pb-3">
        <button
          onClick={() => setActiveCategory('ALL')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeCategory === 'ALL' ? 'bg-hub-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          All ({clients.length})
        </button>
        {CATEGORIES.map(cat => {
          const Icon = cat.icon;
          const count = categoryCounts[cat.value] || 0;
          return (
            <button
              key={cat.value}
              onClick={() => setActiveCategory(cat.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                activeCategory === cat.value ? 'bg-hub-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Icon className={`w-4 h-4 ${activeCategory === cat.value ? 'text-white' : cat.color}`} />
              <span className="hidden sm:inline">{cat.label}</span>
              ({count})
            </button>
          );
        })}
      </div>

      {/* Client List */}
      <div className="space-y-4">
        {filteredClients.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            {activeCategory === 'ALL' 
              ? 'No clients yet. Add your first client to get started.'
              : `No ${getCategoryInfo(activeCategory).label.toLowerCase()} clients yet.`
            }
          </div>
        ) : (
          filteredClients.map(client => {
            const catInfo = getCategoryInfo(client.category || 'CASINO');
            const CatIcon = catInfo.icon;
            
            return (
              <div key={client.id} className="card overflow-hidden">
                <div 
                  className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                  onClick={() => toggleClient(client.id)}
                >
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: client.color || '#E5E7EB' }}
                    >
                      <Building2 className="w-5 h-5 text-gray-700" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold">{client.name}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full bg-gray-100 flex items-center gap-1 ${catInfo.color}`}>
                          <CatIcon className="w-3 h-3" />
                          <span className="hidden sm:inline">{catInfo.label}</span>
                        </span>
                      </div>
                      <p className="text-sm text-gray-500">
                        {client.venues?.length || 0} venues • {client.contacts?.length || 0} contacts
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={(e) => { e.stopPropagation(); setModal({ type: 'client', data: client }); }} 
                      className="btn-ghost p-2"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={(e) => { e.stopPropagation(); deactivateClient(client.id, client.name); }} 
                      className="btn-ghost p-2 text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    {expandedClients[client.id] ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
                  </div>
                </div>
                
                {expandedClients[client.id] && (
                  <div className="border-t bg-gray-50 p-4 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      {client.billingAddress && (
                        <div className="flex items-start gap-2">
                          <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                          <span>{client.billingAddress}</span>
                        </div>
                      )}
                      {client.paymentTerms && (
                        <div className="flex items-start gap-2">
                          <FileText className="w-4 h-4 text-gray-400 mt-0.5" />
                          <span>Payment: {client.paymentTerms}</span>
                        </div>
                      )}
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-medium text-gray-700">Contacts</h4>
                        <button 
                          onClick={() => setModal({ type: 'contact', parentType: 'client', parentId: client.id })}
                          className="text-xs text-hub-600 hover:underline"
                        >
                          + Add Contact
                        </button>
                      </div>
                      <div className="space-y-2">
                        {client.contacts?.map(contact => (
                          <ContactCard 
                            key={contact.id} 
                            contact={contact}
                            onEdit={() => setModal({ type: 'contact', parentType: 'client', parentId: client.id, data: contact })}
                            onDelete={() => deleteContact('client', contact.id)}
                          />
                        ))}
                        {!client.contacts?.length && <p className="text-xs text-gray-400">No contacts yet</p>}
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-medium text-gray-700">Venues</h4>
                        <button 
                          onClick={() => setModal({ type: 'venue', clientId: client.id })}
                          className="text-xs text-hub-600 hover:underline"
                        >
                          + Add Venue
                        </button>
                      </div>
                      <div className="space-y-2">
                        {client.venues?.map(venue => (
                          <div key={venue.id} className="bg-white border rounded-lg overflow-hidden">
                            <div 
                              className="p-3 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                              onClick={() => toggleVenue(venue.id)}
                            >
                              <div className="flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-orange-500" />
                                <div>
                                  <p className="font-medium text-sm">{venue.name}</p>
                                  <p className="text-xs text-gray-500">{venue.locations?.length || 0} locations</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                <button 
                                  onClick={(e) => { e.stopPropagation(); setModal({ type: 'venue', clientId: client.id, data: venue }); }}
                                  className="p-1 hover:bg-gray-100 rounded"
                                >
                                  <Edit2 className="w-3 h-3" />
                                </button>
                                <button 
                                  onClick={(e) => { e.stopPropagation(); deleteVenue(venue.id, venue.name); }}
                                  className="p-1 hover:bg-red-100 rounded text-red-600"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                                {expandedVenues[venue.id] ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                              </div>
                            </div>
                            
                            {expandedVenues[venue.id] && (
                              <div className="border-t bg-gray-50 p-3 space-y-3">
                                {venue.address && (
                                  <p className="text-xs text-gray-600">{venue.address}</p>
                                )}
                                
                                <div>
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-xs font-medium text-gray-600">Contacts</span>
                                    <button 
                                      onClick={() => setModal({ type: 'contact', parentType: 'venue', parentId: venue.id })}
                                      className="text-xs text-hub-600 hover:underline"
                                    >
                                      + Add
                                    </button>
                                  </div>
                                  <div className="space-y-1">
                                    {venue.contacts?.map(contact => (
                                      <ContactCard 
                                        key={contact.id} 
                                        contact={contact}
                                        onEdit={() => setModal({ type: 'contact', parentType: 'venue', parentId: venue.id, data: contact })}
                                        onDelete={() => deleteContact('venue', contact.id)}
                                      />
                                    ))}
                                  </div>
                                </div>

                                <div>
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-xs font-medium text-gray-600">Locations</span>
                                    <button 
                                      onClick={() => setModal({ type: 'location', venueId: venue.id })}
                                      className="text-xs text-hub-600 hover:underline"
                                    >
                                      + Add
                                    </button>
                                  </div>
                                  <div className="flex flex-wrap gap-2">
                                    {venue.locations?.map(loc => (
                                      <span 
                                        key={loc.id} 
                                        className="text-xs bg-white px-2 py-1 rounded border flex items-center gap-1 group cursor-pointer hover:bg-gray-50"
                                        onClick={() => setModal({ type: 'location', venueId: venue.id, data: loc })}
                                      >
                                        {loc.name}
                                        <Edit2 className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100" />
                                      </span>
                                    ))}
                                    {!venue.locations?.length && <p className="text-xs text-gray-400">No locations yet</p>}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                        {!client.venues?.length && <p className="text-xs text-gray-400">No venues yet</p>}
                      </div>
                    </div>

                    {/* Permanent Delete - Owner Only */}
                    {isOwner && (
                      <div className="pt-3 border-t">
                        <button
                          onClick={() => setDeleteModal(client)}
                          className="text-xs text-red-600 hover:text-red-800 hover:underline"
                        >
                          Permanently delete client and all data...
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {modal?.type === 'client' && (
        <ClientModal 
          client={modal.data} 
          onSave={() => { setModal(null); loadClients(); }} 
          onClose={() => setModal(null)} 
        />
      )}
      {modal?.type === 'venue' && (
        <VenueModal 
          venue={modal.data}
          clientId={modal.clientId}
          onSave={() => { setModal(null); loadClients(); }} 
          onClose={() => setModal(null)} 
        />
      )}
      {modal?.type === 'location' && (
        <LocationModal 
          location={modal.data}
          venueId={modal.venueId}
          onSave={() => { setModal(null); loadClients(); }} 
          onClose={() => setModal(null)} 
        />
      )}
      {modal?.type === 'contact' && (
        <ContactModal 
          contact={modal.data}
          parentId={modal.parentId}
          parentType={modal.parentType}
          onSave={() => { setModal(null); loadClients(); }} 
          onClose={() => setModal(null)} 
        />
      )}
      
      {deleteModal && (
        <ConfirmDeleteModal
          name={deleteModal.name}
          type="Client"
          details={[
            `All ${deleteModal.venues?.length || 0} venues`,
            'All locations within those venues',
            'All gigs at those locations',
            'All contractor assignments for those gigs',
            'All contacts'
          ]}
          onConfirm={() => permanentlyDeleteClient(deleteModal.id)}
          onClose={() => setDeleteModal(null)}
        />
      )}
    </div>
  );
}
